import { useCallback, useEffect, useRef, useState } from "react";
import {
  Plug, Power, RefreshCw, Server, Cpu, Database, Boxes,
  MessageSquare, Eye, Gauge, Link2, AlertTriangle, CheckCircle2, XCircle, ShieldAlert, Globe, Laptop,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAppConfig } from "@/context/ConfigContext";
import { AGENT_URL, diagnoseAgent, getAgentStatus, agentConnect, agentDisconnect, AgentHttpError } from "@/lib/agent";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const COMP_META = {
  fastapi: { label: "Backend · FastAPI", icon: Server },
  llama_server: { label: "Motor IA · llama-server", icon: Cpu },
  mongodb: { label: "Base de datos · MongoDB", icon: Database },
};
const COMP_ORDER = ["fastapi", "llama_server", "mongodb"];

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function Dashboard() {
  const { refreshConfig, refreshModels, refreshStatus, config } = useAppConfig();
  const [connState, setConnState] = useState("DESCONECTADO"); // DESCONECTADO | CONECTANDO | CONECTADO
  const [status, setStatus] = useState(null);
  const [diag, setDiag] = useState(null); // { reachable, code, httpStatus?, env }
  const [busy, setBusy] = useState(false);
  const autoRef = useRef(false);

  const applyStatus = useCallback((data) => {
    setStatus(data);
    setConnState(data.connection === "CONNECTED" ? "CONECTADO" : "DESCONECTADO");
  }, []);

  // Comprueba el agente y, si es accesible, trae el estado. Reutilizable (mount + botones).
  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      const d = await diagnoseAgent();
      setDiag(d);
      if (d.reachable && d.code === "ok") {
        try {
          const s = await getAgentStatus();
          applyStatus(s);
        } catch (e) {
          const code = e instanceof AgentHttpError ? "http_error" : "refused";
          setDiag({ reachable: false, code, httpStatus: e?.status, env: d.env });
          setStatus(null);
          setConnState("DESCONECTADO");
        }
      } else {
        setStatus(null);
        setConnState("DESCONECTADO");
      }
      return d;
    } finally {
      setBusy(false);
    }
  }, [applyStatus]);

  // Auto-conexión al abrir la app: reintentos con backoff durante ~28s.
  // Mientras tanto se muestra "Conectando..." (no un ERROR alarmante).
  const autoConnect = useCallback(async () => {
    setBusy(true);
    setConnState("CONECTANDO");
    setDiag(null);
    const deadline = Date.now() + 28000;
    let delay = 1200;
    try {
      while (Date.now() < deadline) {
        let d;
        try {
          d = await diagnoseAgent();
        } catch {
          d = { reachable: false, code: "refused" };
        }
        if (d.reachable && d.code === "ok") {
          try {
            const data = await agentConnect();
            applyStatus(data);
            setDiag({ reachable: true, code: "ok", env: d.env });
            refreshConfig();
            refreshModels();
            refreshStatus();
            if (data.connection === "CONNECTED") toast.success("KRAI LOCAL AI conectado");
          } catch (e) {
            setConnState("DESCONECTADO");
            setDiag({
              reachable: false,
              code: e instanceof AgentHttpError ? "http_error" : "refused",
              httpStatus: e?.status,
              env: d.env,
            });
          }
          return;
        }
        if (d.reachable && d.code === "http_error") {
          setConnState("DESCONECTADO");
          setDiag(d);
          return;
        }
        await sleep(delay);
        delay = Math.min(Math.round(delay * 1.6), 4000);
      }
      // Agotados los reintentos: mostrar estado calmado "no disponible".
      let d;
      try {
        d = await diagnoseAgent();
      } catch {
        d = { reachable: false, code: "refused" };
      }
      setDiag(d);
      setConnState("DESCONECTADO");
    } finally {
      setBusy(false);
    }
  }, [applyStatus, refreshConfig, refreshModels, refreshStatus]);

  useEffect(() => {
    if (autoRef.current) return;
    autoRef.current = true;
    autoConnect();
  }, [autoConnect]);

  const handleConnect = async () => {
    setBusy(true);
    setConnState("CONECTANDO");
    try {
      // 1) ping / diagnóstico
      const d = await diagnoseAgent();
      setDiag(d);
      if (!(d.reachable && d.code === "ok")) {
        setConnState("DESCONECTADO");
        toastForDiag(d);
        return;
      }
      // 2) connect
      const data = await agentConnect();
      applyStatus(data);
      setDiag({ reachable: true, code: "ok", env: d.env });
      // 3) refrescar datos del backend para Chat/Vision/Models
      refreshConfig();
      refreshModels();
      refreshStatus();
      if (data.connection === "CONNECTED") toast.success("KRAI LOCAL AI conectado");
      else toast.error("Servicios iniciados con errores: revisa el detalle");
    } catch (e) {
      setConnState("DESCONECTADO");
      if (e instanceof AgentHttpError) {
        setDiag((prev) => ({ reachable: false, code: "http_error", httpStatus: e.status, env: prev?.env }));
        toast.error(`El agente respondió con error HTTP ${e.status}`);
      } else {
        setDiag((prev) => ({ reachable: false, code: prev?.env?.crossScheme ? "blocked_policy" : "refused", env: prev?.env }));
        toast.error("No se pudo contactar con el agente local");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    setBusy(true);
    try {
      const data = await agentDisconnect();
      applyStatus(data);
      setDiag({ reachable: true, code: "ok", env: diag?.env });
      toast.success("Servicios detenidos por el agente");
    } catch (e) {
      const d = await diagnoseAgent();
      setDiag(d);
      toastForDiag(d);
    } finally {
      setBusy(false);
    }
  };

  const isConnecting = connState === "CONECTANDO" || busy;
  const model = status?.model;
  const caps = status?.capabilities;
  const showBanner = diag && !(diag.reachable && diag.code === "ok") && connState !== "CONECTANDO";

  return (
    <div className="krai-enter">
      <PageHeader
        title="Dashboard"
        subtitle="Panel de control · el agente local es el puente con los servicios del Mac"
        action={
          <Button
            data-testid="refresh-status-button"
            variant="secondary"
            onClick={refresh}
            disabled={isConnecting}
            className="rounded-sm border border-[#262626] bg-transparent text-white hover:bg-[#1a1a1a]"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${busy ? "animate-spin" : ""}`} />
            Actualizar estado
          </Button>
        }
      />

      {/* Connection control */}
      <div className="mb-4 rounded-md border border-[#262626] bg-[#121212] p-5" data-testid="connection-panel">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <ConnStateIndicator state={connState} />
            <EnvBadge env={diag?.env} />
          </div>
          <div className="flex gap-2">
            {connState !== "CONECTADO" ? (
              <Button
                data-testid="conectar-button"
                onClick={handleConnect}
                disabled={isConnecting}
                className="rounded-sm bg-emerald-500 text-black hover:bg-emerald-400"
              >
                <Plug className={`mr-2 h-4 w-4 ${isConnecting ? "animate-pulse" : ""}`} />
                {isConnecting ? "Conectando…" : "Conectar"}
              </Button>
            ) : (
              <Button
                data-testid="desconectar-button"
                onClick={handleDisconnect}
                disabled={busy}
                className="rounded-sm border border-red-500/50 bg-transparent text-red-400 hover:bg-red-500/10"
              >
                <Power className="mr-2 h-4 w-4" />
                Desconectar
              </Button>
            )}
          </div>
        </div>

        {connState === "CONECTANDO" && (
          <p className="mt-3 flex items-center gap-2 font-mono text-xs text-amber-300" data-testid="connecting-hint">
            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
            Conectando con KRAI LOCAL AGENT…
          </p>
        )}

        {showBanner && <DiagnosticBanner diag={diag} onRetry={handleConnect} busy={busy} />}
      </div>

      {/* Component status grid */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-md border border-[#262626] bg-[#121212] p-4" data-testid="components-card">
          <SectionLabel>Componentes</SectionLabel>
          <div className="mt-3 divide-y divide-[#1a1a1a]">
            {COMP_ORDER.map((key) => {
              const meta = COMP_META[key];
              const c = status?.components?.[key];
              return (
                <StatusRow
                  key={key}
                  testid={`component-${key}`}
                  icon={meta.icon}
                  label={meta.label}
                  ok={c?.running}
                  unknown={!status}
                  detail={c?.error || c?.detail}
                />
              );
            })}
          </div>
        </div>

        <div className="rounded-md border border-[#262626] bg-[#121212] p-4" data-testid="capabilities-card">
          <SectionLabel>Modelo y capacidades</SectionLabel>
          <div className="mt-3 divide-y divide-[#1a1a1a]">
            <StatusRow testid="model-row" icon={Boxes} label="Modelo" ok={model?.available} unknown={!status}
              value={model?.id || (status ? "no disponible" : "—")} />
            <StatusRow testid="chat-cap-row" icon={MessageSquare} label="Chat" ok={caps?.chat} unknown={!status}
              value={caps?.chat ? "Disponible" : status ? "No disponible" : "—"} />
            <StatusRow testid="vision-cap-row" icon={Eye} label="Vision" ok={caps?.vision} unknown={!status}
              value={caps?.vision ? "Disponible" : status ? "No disponible" : "—"} />
            <StatusRow testid="response-time-row" icon={Gauge} label="Tiempo de respuesta" ok={status?.response_time_ms != null}
              unknown={!status} value={status?.response_time_ms != null ? `${status.response_time_ms} ms` : "—"} neutral />
          </div>
        </div>
      </div>

      {/* Errors reales del agente */}
      {status?.errors?.length > 0 && (
        <div data-testid="connection-errors" className="mt-4 rounded-md border border-red-500/40 bg-red-500/10 p-4">
          <div className="flex items-center gap-2 text-red-400">
            <AlertTriangle className="h-4 w-4" />
            <span className="font-mono text-xs uppercase tracking-widest">No se puede conectar · errores reales</span>
          </div>
          <ul className="mt-2 space-y-1">
            {status.errors.map((e, i) => (
              <li key={`${i}-${e}`} className="break-all font-mono text-sm text-red-200">• {e}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Configured URL */}
      <div className="mt-4 rounded-md border border-[#262626] bg-[#121212] p-4" data-testid="configured-url-card">
        <div className="flex items-center gap-2 text-[#737373]">
          <Link2 className="h-4 w-4" />
          <span className="font-mono text-xs uppercase tracking-widest">URL del motor local (backend)</span>
        </div>
        <div className="mt-2 break-all font-mono text-sm text-[#f5f5f5]">{config?.base_url || "—"}</div>
      </div>
    </div>
  );
}

function toastForDiag(d) {
  switch (d?.code) {
    case "http_error":
      toast.error(`El agente respondió con error HTTP ${d.httpStatus}`);
      break;
    case "timeout":
      toast.error("Tiempo de espera agotado al contactar el agente");
      break;
    case "blocked_policy":
    case "blocked_cors":
      toast.error("El navegador bloqueó el acceso a la red local. Permite el acceso y reintenta.");
      break;
    default:
      toast.error("Agente local no detectado en " + AGENT_URL);
  }
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-[#a3a3a3]">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

function SectionLabel({ children }) {
  return <span className="font-mono text-[11px] uppercase tracking-widest text-[#737373]">{children}</span>;
}

function EnvBadge({ env }) {
  if (!env) return null;
  const local = env.mode === "local";
  const Icon = local ? Laptop : Globe;
  return (
    <span
      data-testid="env-badge"
      className={cn(
        "inline-flex items-center gap-1.5 rounded-sm border px-2 py-1 font-mono text-[11px] uppercase tracking-wider",
        local ? "border-emerald-500/40 text-emerald-300" : "border-sky-500/40 text-sky-300"
      )}
      title={`origin: ${env.origin} · agente: ${env.agentUrl}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {local ? "localhost" : "preview / publicada"}
    </span>
  );
}

const STATE_STYLES = {
  DESCONECTADO: { dot: "bg-neutral-500", text: "text-[#a3a3a3]", ring: "border-neutral-500/40" },
  CONECTANDO: { dot: "bg-amber-400 animate-pulse", text: "text-amber-300", ring: "border-amber-500/40" },
  CONECTADO: { dot: "bg-emerald-400", text: "text-emerald-300", ring: "border-emerald-500/40" },
};

function ConnStateIndicator({ state }) {
  const s = STATE_STYLES[state] || STATE_STYLES.DESCONECTADO;
  return (
    <div className="flex items-center gap-3" data-testid="connection-state">
      <span className={cn("inline-flex h-11 w-11 items-center justify-center rounded-md border", s.ring)}>
        <span className={cn("h-3 w-3 rounded-full", s.dot)} />
      </span>
      <div>
        <div className="font-mono text-[11px] uppercase tracking-widest text-[#737373]">Estado</div>
        <div className={cn("font-heading text-xl font-bold tracking-tight", s.text)} data-testid="connection-state-label">
          {state}
        </div>
      </div>
    </div>
  );
}

function DiagnosticBanner({ diag, onRetry, busy }) {
  const env = diag?.env;
  const origin = env?.origin;
  const isPolicy = diag.code === "blocked_policy" || diag.code === "blocked_cors" || (diag.code === "refused" && env?.crossScheme);
  const isHttp = diag.code === "http_error";
  const isTimeout = diag.code === "timeout";

  let tone = "amber";
  let Icon = AlertTriangle;
  let title = "Agente local no disponible";
  let body = null;

  if (isHttp) {
    tone = "red";
    Icon = XCircle;
    title = `El agente respondió con error HTTP ${diag.httpStatus}`;
    body = <p>Es un error real del agente, no del navegador. Revisa la ventana del lanzador en tu Mac.</p>;
  } else if (isPolicy) {
    tone = "sky";
    Icon = ShieldAlert;
    title = "Agente local no disponible o acceso a la red local bloqueado";
    body = (
      <div className="space-y-2">
        <p>
          Esta app se ejecuta en <span className="font-mono">{origin}</span> (HTTPS) e intenta contactar con el agente
          local en <span className="font-mono">{AGENT_URL}</span>. Chrome exige permiso de <b>Acceso a la red local
          (Private Network Access)</b> para ello. Esto <b>no</b> significa que el agente esté apagado.
        </p>
        <ol className="ml-4 list-decimal space-y-1 text-[13px]">
          <li>Comprueba que el agente está instalado y en marcha en tu Mac (<span className="font-mono">installer/install_mac_agent.sh</span>).</li>
          <li>En Chrome: icono del candado/ajustes del sitio → permite <b>“Acceso a la red local”</b> para este sitio.</li>
          <li>
            Verifica que el origen <span className="font-mono">{origin}</span> está en <span className="font-mono">allowed_origins</span> del agente.
          </li>
          <li>Pulsa <b>Reintentar conexión</b>.</li>
        </ol>
      </div>
    );
  } else if (isTimeout) {
    title = "Tiempo de espera agotado";
    body = <p>El agente no respondió a tiempo en <span className="font-mono">{AGENT_URL}</span>. Reintenta.</p>;
  } else {
    // refused en localhost => realmente apagado
    body = (
      <div className="space-y-2">
        <p>
          No hay respuesta en <span className="font-mono">{AGENT_URL}</span>. Instala el agente en segundo plano (una vez):
        </p>
        <code className="block rounded-sm bg-black/40 px-2 py-1 font-mono text-xs">cd installer && ./install_mac_agent.sh</code>
        <p className="text-[13px] opacity-80">O arráncalo manualmente: <span className="font-mono">cd launcher && ./KRAI-LOCAL-AI.command</span></p>
      </div>
    );
  }

  const toneCls = {
    amber: "border-amber-500/40 bg-amber-500/10 text-amber-200",
    sky: "border-sky-500/40 bg-sky-500/10 text-sky-100",
    red: "border-red-500/40 bg-red-500/10 text-red-200",
  }[tone];
  const iconCls = { amber: "text-amber-400", sky: "text-sky-400", red: "text-red-400" }[tone];

  return (
    <div data-testid="agent-diagnostic" data-diag-code={diag.code} className={cn("mt-4 rounded-sm border p-3", toneCls)}>
      <div className="flex items-start gap-3">
        <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", iconCls)} />
        <div className="min-w-0 flex-1 text-sm">
          <div className="font-medium">{title}</div>
          <div className="mt-1 opacity-90">{body}</div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Button
              data-testid="retry-connection-button"
              size="sm"
              onClick={onRetry}
              disabled={busy}
              className="h-8 rounded-sm bg-white text-black hover:bg-neutral-200"
            >
              <RefreshCw className={cn("mr-2 h-3.5 w-3.5", busy && "animate-spin")} />
              Reintentar conexión
            </Button>
            <span className="font-mono text-[11px] opacity-60" data-testid="diag-detail">
              diag={diag.code} · entorno={env?.mode} · agente={AGENT_URL}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusRow({ icon: Icon, label, ok, unknown, value, detail, testid, neutral }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5" data-testid={testid}>
      <div className="flex min-w-0 items-center gap-2.5">
        <Icon className="h-4 w-4 shrink-0 text-[#737373]" />
        <div className="min-w-0">
          <div className="truncate text-sm text-[#e5e5e5]">{label}</div>
          {detail && <div className="truncate font-mono text-[11px] text-[#737373]">{detail}</div>}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {value && <span className="max-w-[180px] truncate font-mono text-xs text-[#a3a3a3]">{value}</span>}
        {unknown ? (
          <span className="h-2.5 w-2.5 rounded-full bg-neutral-600" data-testid={`${testid}-dot`} />
        ) : neutral ? (
          <span className={cn("h-2.5 w-2.5 rounded-full", ok ? "bg-emerald-400" : "bg-neutral-600")} data-testid={`${testid}-dot`} />
        ) : ok ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-400" data-testid={`${testid}-dot`} />
        ) : (
          <XCircle className="h-4 w-4 text-red-400" data-testid={`${testid}-dot`} />
        )}
      </div>
    </div>
  );
}
